import React, { useMemo, useCallback, useRef } from 'react';
import { Message, Box, Typography } from 'fbm-ui';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useAppSelector } from '../../redux/hooks';
import { log } from '@avocadoui/utils';
import { SxProps } from '@mui/material';
import type { TXDeviceManager } from '../../types/txlive-pusher';
import {
  PUSHER_WARNING_CODES,
  CAMERA_REQUIRED_ERROR_CODES,
  FATAL_PUSHER_ERROR_CODES,
  VIDEO_QUALITY_CONFIG,
  AUDIO_QUALITY_CONFIG,
  VIDEO_RESOLUTION_CONFIG,
  ERROR_MESSAGES,
} from '../../constants/pusher';
import {
  getPusherUnavailableError,
  getTXLivePusherConstructor,
} from './pusher.utils';

type PusherProps = {
  liveUrl?: string;
  onPusherError?: (err: any) => void;
  sx?: SxProps;
  orientation?: 'portrait' | 'landscape';
};

/**
 * Pusher 组件暴露的方法接口
 */
export interface PusherHandle {
  /** 停止推流 */
  stopPush: () => void;
  /** 开始推流 */
  startPush: () => void;
  /** 切换摄像头开关 */
  toggleCamera: () => void;
  /** 切换麦克风开关 */
  toggleMic: () => void;
  /** 获取设备列表 */
  getDevicesList: () => Promise<MediaDeviceInfo[]>;
}

const Pusher = forwardRef<PusherHandle, PusherProps>(
  (
    { liveUrl = '', onPusherError, sx, orientation = 'landscape' }: PusherProps,
    ref
  ) => {
    const selectedCameraId = useAppSelector(
      (state) => state.room.selectedCameraId
    );
    const selectedMicId = useAppSelector((state) => state.room.selectedMicId);

    const videoStreamIdRef = useRef<string | null>(null);
    const audioStreamIdRef = useRef<string | null>(null);
    const sdkUnavailableError = useMemo(() => getPusherUnavailableError(), []);

    // 创建推流实例
    const pushClient = useMemo(() => {
      const TXLivePusherConstructor = getTXLivePusherConstructor();
      if (!TXLivePusherConstructor) {
        return null;
      }

      try {
        return new TXLivePusherConstructor();
      } catch (error) {
        log.capture('Pusher_Init_Error', { error });
        return null;
      }
    }, []);

    // 缓存设备管理器
    const deviceManager = useMemo<TXDeviceManager | null>(
      () => pushClient?.getDeviceManager() ?? null,
      [pushClient]
    );

    const handlePusherError = useCallback(
      (err: any) => {
        const { errorCode, message } = err;
        log.capture('Pusher_Error', {
          code: errorCode,
          message: message,
        });

        // 获取友好的错误消息
        const errorMsg =
          ERROR_MESSAGES[errorCode as keyof typeof ERROR_MESSAGES] ||
          message ||
          ERROR_MESSAGES.DEFAULT;
        Message.error(errorMsg);

        // 如果是摄像头/麦克风相关的严重错误，或 SDK 缺失等致命错误，通知父组件
        if (
          CAMERA_REQUIRED_ERROR_CODES.includes(errorCode) ||
          FATAL_PUSHER_ERROR_CODES.includes(
            errorCode as typeof FATAL_PUSHER_ERROR_CODES[number]
          )
        ) {
          onPusherError?.(err);
        }
      },
      [onPusherError]
    );

    useEffect(() => {
      if (pushClient) {
        return;
      }

      log.capture('Pusher_SDK_Unavailable', sdkUnavailableError);
      handlePusherError(sdkUnavailableError);
    }, [handlePusherError, pushClient, sdkUnavailableError]);
    // 推流状态更新回调
    const handlePushStatusUpdate = useCallback(
      (status: number, message: string) => {
        log.capture('Pusher_Status', { status, message });
      },
      []
    );

    // 推流统计数据回调
    const handleStatisticsUpdate = useCallback(() => {
      // 可以在这里处理统计数据
    }, []);

    // 推流错误回调
    const handleError = useCallback(
      (code: number, message: string, extraInfo?: any) => {
        log.capture('Pusher_Error_Observer', { code, message, extraInfo });
        const err = { errorCode: code, message };
        handlePusherError(err);
        // 触发面试中断页面
        onPusherError?.(err);
      },
      [handlePusherError, onPusherError]
    );

    // 首帧音频采集完成回调
    const handleCaptureFirstAudioFrame = useCallback(() => {
      log.capture('Pusher_FirstAudioFrame', {
        message: '首帧音频采集完成',
      });
    }, []);

    // 首帧视频采集完成回调
    const handleCaptureFirstVideoFrame = useCallback(() => {
      log.capture('Pusher_FirstVideoFrame', {
        message: '首帧视频采集完成',
      });
    }, []);

    // 推流警告回调
    const handleWarning = useCallback(
      (code: number, message: string, data: any) => {
        log.capture('Pusher_Warning', { code, message, data });

        if (!deviceManager || !pushClient) {
          onPusherError?.({ errorCode: code, message });
          return;
        }

        if (code === PUSHER_WARNING_CODES.CAMERA_INTERRUPTED) {
          // 摄像头中断，查找剩余可用摄像头
          deviceManager.getDevicesList('video').then((devices) => {
            if (devices.length > 0 && devices[0].deviceId) {
              // 切换摄像头设备
              deviceManager.switchCamera(devices[0].deviceId, data.streamId);
              log.capture('Pusher_Camera_Error', {
                message: '摄像头采集异常中断，自动切换',
              });
            } else {
              // 结束推流
              if (pushClient.isPushing()) {
                pushClient.stopPush();
                log.capture('Pusher_Camera_Error', {
                  message: '摄像头采集异常中断，推流结束',
                });
              }
              // 停止采集
              pushClient.stopCamera();
              log.capture('Pusher_Camera_Error', {
                message: '停止采集摄像头',
              });
              // 触发面试中断页面
              onPusherError?.({ errorCode: code, message });
            }
          });
        } else if (code === PUSHER_WARNING_CODES.MICROPHONE_INTERRUPTED) {
          // 麦克风中断，查找剩余可用麦克风
          deviceManager.getDevicesList('audio').then((devices) => {
            if (devices.length > 0 && devices[0].deviceId) {
              // 切换麦克风设备
              deviceManager.switchMicrophone(
                devices[0].deviceId,
                data.streamId
              );
              log.capture('Pusher_Mic_Error', {
                message: '麦克风采集异常中断，自动切换',
              });
            } else {
              // 结束推流
              if (pushClient.isPushing()) {
                pushClient.stopPush();
                log.capture('Pusher_Mic_Error', {
                  message: '麦克风采集异常中断，推流结束',
                });
              }
              // 停止采集
              pushClient.stopMicrophone();
              log.capture('Pusher_Mic_Error', {
                message: '停止采集麦克风',
              });
              // 触发面试中断页面
              onPusherError?.({ errorCode: code, message });
            }
          });
        } else if (
          CAMERA_REQUIRED_ERROR_CODES.includes(
            code as typeof CAMERA_REQUIRED_ERROR_CODES[number]
          )
        ) {
          // 其他严重错误，直接触发面试中断
          onPusherError?.({ errorCode: code, message });
        }
      },
      [deviceManager, pushClient, onPusherError]
    );

    // 设置推流观察者
    useEffect(() => {
      if (!pushClient) {
        return;
      }

      pushClient.setObserver({
        onError: handleError,
        onWarning: handleWarning,
        onPushStatusUpdate: handlePushStatusUpdate,
        onStatisticsUpdate: handleStatisticsUpdate,
        onCaptureFirstAudioFrame: handleCaptureFirstAudioFrame,
        onCaptureFirstVideoFrame: handleCaptureFirstVideoFrame,
      });

      return () => {
        // 清理观察者
        pushClient.setObserver({});
      };
    }, [
      pushClient,
      handleError,
      handleWarning,
      handlePushStatusUpdate,
      handleStatisticsUpdate,
      handleCaptureFirstAudioFrame,
      handleCaptureFirstVideoFrame,
    ]);

    useEffect(() => {
      if (!liveUrl || !pushClient) return;

      pushClient.setRenderView('videoContainer');
      pushClient.videoView.muted = true;

      // 设置视频质量
      pushClient.setVideoQuality(VIDEO_QUALITY_CONFIG.QUALITY);
      // 设置音频质量
      pushClient.setAudioQuality(AUDIO_QUALITY_CONFIG.QUALITY);
      // 自定义设置帧率
      pushClient.setProperty('setVideoFPS', VIDEO_QUALITY_CONFIG.FPS);
      pushClient.setProperty('setVideoBitrate', VIDEO_QUALITY_CONFIG.BITRATE);
      // 启用回声消除
      pushClient.setProperty('enableAudioAEC', AUDIO_QUALITY_CONFIG.ENABLE_AEC);
      // 启用噪声抑制
      pushClient.setProperty('enableAudioANS', AUDIO_QUALITY_CONFIG.ENABLE_ANS);

      // 设置分辨率
      const resolution =
        orientation === 'portrait'
          ? VIDEO_RESOLUTION_CONFIG.PORTRAIT
          : VIDEO_RESOLUTION_CONFIG.LANDSCAPE;
      pushClient.setProperty('setVideoResolution', resolution);

      Promise.all([
        pushClient.startCamera(selectedCameraId),
        pushClient.startMicrophone(selectedMicId),
      ])
        .then(([videoStreamId, audioStreamId]) => {
          videoStreamIdRef.current = videoStreamId;
          audioStreamIdRef.current = audioStreamId;
        })
        .catch(handlePusherError);

      return () => {
        log.capture('Pusher_Cleanup', { message: '清理推流资源' });
        pushClient.stopMicrophone();
        pushClient.stopCamera();
        pushClient.stopPush();
      };
    }, [
      handlePusherError,
      liveUrl,
      orientation,
      selectedCameraId,
      selectedMicId,
      pushClient,
    ]);

    // 组件卸载时销毁推流实例
    useEffect(() => {
      const client = pushClient;
      return () => {
        client?.destroy();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        stopPush: () => {
          if (!pushClient) {
            return;
          }
          log.capture('Pusher_Stop', { message: '停止推流' });
          pushClient.stopMicrophone();
          pushClient.stopCamera();
          pushClient.stopPush();
        },
        startPush: () => {
          if (!pushClient) {
            handlePusherError(sdkUnavailableError);
            return;
          }
          log.capture('Pusher_Start', { message: '开始推流' });
          pushClient.startPush(liveUrl).catch(handlePusherError);
        },
        toggleCamera: () => {
          if (!pushClient) {
            handlePusherError(sdkUnavailableError);
            return;
          }
          log.capture('Pusher_ToggleCamera', { message: '切换摄像头' });
          if (videoStreamIdRef.current) {
            videoStreamIdRef.current = null;
            pushClient.stopCamera();
          } else {
            pushClient
              .startCamera()
              .then((videoStreamId) => {
                videoStreamIdRef.current = videoStreamId;
              })
              .catch(handlePusherError);
          }
        },
        toggleMic: () => {
          if (!pushClient) {
            handlePusherError(sdkUnavailableError);
            return;
          }
          log.capture('Pusher_ToggleMic', { message: '切换麦克风' });
          if (audioStreamIdRef.current) {
            audioStreamIdRef.current = null;
            pushClient.stopMicrophone();
          } else {
            pushClient
              .startMicrophone()
              .then((audioStreamId) => {
                audioStreamIdRef.current = audioStreamId;
              })
              .catch(handlePusherError);
          }
        },
        getDevicesList: () => {
          return deviceManager?.getDevicesList() ?? Promise.resolve([]);
        },
      }),
      [
        deviceManager,
        handlePusherError,
        liveUrl,
        pushClient,
        sdkUnavailableError,
      ]
    );

    // 切换摄像头设备
    useEffect(() => {
      if (deviceManager && selectedCameraId && selectedCameraId !== 'default') {
        try {
          deviceManager.switchCamera(selectedCameraId);
        } catch (error) {
          log.capture('Pusher_SwitchCamera_Error', {
            error,
            deviceId: selectedCameraId,
          });
        }
      }
    }, [selectedCameraId, deviceManager]);

    // 切换麦克风设备
    useEffect(() => {
      if (deviceManager && selectedMicId && selectedMicId !== 'default') {
        try {
          deviceManager.switchMicrophone(selectedMicId);
        } catch (error) {
          log.capture('Pusher_SwitchMicrophone_Error', {
            error,
            deviceId: selectedMicId,
          });
        }
      }
    }, [selectedMicId, deviceManager]);

    if (!pushClient) {
      return (
        <Box
          id="videoContainer"
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2,
            textAlign: 'center',
            ...sx,
          }}
        >
          <Typography variant="body2">{sdkUnavailableError.message}</Typography>
        </Box>
      );
    }

    return (
      <Box
        id="videoContainer"
        sx={{ width: '100%', height: '100%', ...sx }}
      ></Box>
    );
  }
);

export default React.memo(Pusher);
